import React from 'react'
import ReactDOM from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import type { AuthenticationResult } from '@azure/msal-browser'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { msalConfig, googleConfig } from './config/authConfig'
import { initAudioEncoder } from './lib/audioEncoder'
import {
  initTelemetry,
  trackAuthEvent,
  trackAuthException,
  redirectHashFlags,
  hasMsalAuthStateCookie,
  browserContext,
} from './lib/telemetry'
import { clearStaleInteractionStatus, readInteractionStatus } from './lib/msalCleanup'
import { captureAuthError } from './lib/authError'
import App from './App'
import './index.css'

// Initialize audio encoder for WAV recording support
initAudioEncoder().catch(err => {
  console.warn('Audio encoder init failed, voice recording may not work:', err)
})

// Create MSAL instance for Entra ID authentication
const msalInstance = new PublicClientApplication(msalConfig)
const clientId = msalConfig.auth.clientId

// Initialize MSAL and render app with both auth providers
msalInstance.initialize().then(async () => {
  // Wire telemetry before processing the redirect so MSAL events from
  // handleRedirectPromise are captured.
  initTelemetry(msalInstance)

  // Process any redirect response (login or logout)
  // Required for MSAL to complete the auth flow after redirect.
  // Snapshot the incoming hash first: on a successful redirect MSAL clears it
  // (RedirectClient.clearHash), and on the silent-null failure it short-circuits
  // before ever reading the hash — so capturing it up front is what makes the
  // flags reliable on every path.
  const hashAtEntry = window.location.hash
  let redirectResult: AuthenticationResult | null = null
  try {
    redirectResult = await msalInstance.handleRedirectPromise()
  } catch (error) {
    trackAuthException(error, { source: 'handleRedirectPromise' })
    captureAuthError(error, 'handleRedirectPromise')
    // Defensive: clear msal.interaction.status if the failed flow left it
    // behind. We log whether the value matched the current clientId so the
    // dashboard can distinguish "cleaned up our own stale state" (matched)
    // from "clobbered something unfamiliar" (mismatched orphan).
    const valueBefore = readInteractionStatus()
    if (clearStaleInteractionStatus()) {
      trackAuthEvent('auth.cleanupCleared', {
        source: 'handleRedirectPromise',
        valueMatchedClientId: valueBefore === clientId,
      })
    }
    console.error('[MSAL] Redirect handling failed:', error)
  }

  // The dominant failure mode throws nothing: handleRedirectPromise resolves
  // null while the URL still carries `#code=…`, because the request state was
  // wiped from sessionStorage during the hop to Entra. The catch above can't
  // see it. Emit only on a real redirect return (hash carried a response, or we
  // got a result) so plain page loads stay quiet. `resolvedNull && hashHadCode`
  // is the bug; `cookieFallbackSurvived` tells us whether the cookie fix held.
  const hashFlags = redirectHashFlags(hashAtEntry)
  if (hashFlags.hashHadCode || hashFlags.hashHadError || redirectResult !== null) {
    trackAuthEvent('auth.redirectResolved', {
      resolvedNull: redirectResult === null,
      ...hashFlags,
      cookieFallbackSurvived: hasMsalAuthStateCookie(),
      ...browserContext(),
    })
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <GoogleOAuthProvider clientId={googleConfig.clientId}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </GoogleOAuthProvider>
    </React.StrictMode>
  )
})

